import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../../components/ui/dialog";
import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Plus, Edit } from "lucide-react";
import { DatePickerWithRange } from "../../../../components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import toastifyInstance from "../../../../utils/toastify";
import { createDayOff, updateDayOff } from "../../../../api/dayoff";
import { useQueryClient } from "@tanstack/react-query";

type DayOff = {
    _id: string;
    startDate: string;
    endDate: string;
    reason: string;
    note: string;
}
const schema = yup.object().shape({
    reason: yup.string().required("Reason is required"),
    note: yup.string().optional()
});

type FormData = {
    reason: string;
    note?: string;
};

const CreateDayOff = ({ item, update = false }: { item?: DayOff, update?: boolean }) => {
    const [open, setOpen] = useState(false);
    const [dateRange, setDateRange] = useState<DateRange | undefined>(item ? {
        from: new Date(item.startDate),
        to: new Date(item.endDate)
    } : undefined);
    const [dateError, setDateError] = useState<string>("");

    const queryClient = useQueryClient();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            reason: item?.reason || "",
            note: item?.note || ""
        }
    });

    const validateDateRange = () => {
        if (!dateRange?.from || !dateRange?.to) {
            setDateError("Start date and end date are required");
            return false;
        }
        setDateError("");
        return true;
    };

    const onSubmit = async (data: FormData) => {
        if (!validateDateRange()) return;
        if (!dateRange?.from || !dateRange?.to) return;

        try {
            const submitData = {
                ...data,
                startDate: dateRange.from,
                endDate: dateRange.to
            };
            if (update && item) {
                await updateDayOff(item._id, submitData);
            } else {
                await createDayOff(submitData);
            }
            toastifyInstance.success(update ? "Day off updated successfully" : "Day off created successfully");
            queryClient.invalidateQueries({ queryKey: ["day-off"] });
            setOpen(false);
            reset();
            setDateRange(undefined);
        } catch (error) {
            console.error(error);
            toastifyInstance.error(update ? "Failed to update day off" : "Failed to create day off");
        }
    };

    const handleClose = (open: boolean) => {
        setOpen(open);
        if (!open) {
            reset();
            setDateRange(undefined);
            setDateError("");
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogTrigger asChild>
                {
                    update ? (
                        <Button className="rounded-full active:shadow-inner" size="icon">
                            <Edit className="text-blue-500" />
                        </Button>
                    ) : (
                        <Button size="icon">
                            <Plus />
                        </Button>
                    )
                }
            </DialogTrigger>
            <DialogContent className="bg-white w-[90%] max-w-[100%]">
                <DialogHeader className="w-full flex justify-center">
                    <DialogTitle className="text-2xl font-bold text-center">{update ? "Update Day Off" : "Create Day Off"}</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                        <div>
                            <Label>Date Range</Label>
                            <DatePickerWithRange date={dateRange} setDate={setDateRange} />
                            {dateError && (
                                <p className="text-red-500 text-sm mt-1">{dateError}</p>
                            )}
                        </div>

                        <div>
                            <Label>Reason</Label>
                            <Input
                                placeholder="Enter reason"
                                {...register("reason")}
                            />
                            {errors.reason && (
                                <p className="text-red-500 text-sm mt-1">{errors.reason.message}</p>
                            )}
                        </div>

                        <div>
                            <Label>Note (Optional)</Label>
                            <Textarea
                                placeholder="Enter note"
                                {...register("note")}
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button type="submit">{update ? "Update" : "Create"}</Button>
                        </div>
                    </form>
                </DialogDescription>
            </DialogContent>
        </Dialog>
    );
};

export default CreateDayOff;